import React, { useState, useEffect, useRef, useCallback } from 'react';
import { FaTimes, FaSpinner, FaDatabase, FaPlay, FaCheck } from 'react-icons/fa';
import api from '../../../services/api';
import { highlightSQL } from './syntaxHighlighting';
import ThemedSelect from '../../common/ThemedSelect';

const SQL_KEYWORDS = [
  'SELECT',
  'FROM',
  'WHERE',
  'AND',
  'OR',
  'NOT',
  'IN',
  'LIKE',
  'BETWEEN',
  'JOIN',
  'LEFT',
  'RIGHT',
  'INNER',
  'OUTER',
  'FULL',
  'CROSS',
  'ON',
  'GROUP',
  'BY',
  'ORDER',
  'ASC',
  'DESC',
  'HAVING',
  'LIMIT',
  'OFFSET',
  'AS',
  'DISTINCT',
  'TOP',
  'WITH',
  'UNION',
  'ALL',
  'EXISTS',
  'CASE',
  'WHEN',
  'THEN',
  'ELSE',
  'END',
  'IS',
  'NULL',
  'COUNT',
  'SUM',
  'AVG',
  'MIN',
  'MAX',
  'CAST',
  'CONVERT',
  'COALESCE',
  'ISNULL',
  'OVER',
  'PARTITION',
  'ROW_NUMBER',
  'RANK',
  'DENSE_RANK',
];

const CreateSharedQueryModal = ({ isOpen, onClose, onCreated }) => {
  const [activeTab, setActiveTab] = useState('details');
  const [loading, setLoading] = useState(false);
  const [validating, setValidating] = useState(false);
  const [testResults, setTestResults] = useState(null);
  const [error, setError] = useState(null);
  const [validationResult, setValidationResult] = useState(null);
  const [dataSources, setDataSources] = useState([]);
  const [lineCount, setLineCount] = useState(1);
  const textareaRef = useRef(null);
  const highlightRef = useRef(null);
  const lineNumberRef = useRef(null);

  const [formData, setFormData] = useState({
    queryName: '',
    description: '',
    sqlQuery:
      '-- Write your SQL query here\nSELECT TOP 100\n  *\nFROM LexiBridgeDataView\nWHERE 1=1',
    dataSourceId: 1,
    isPublic: true,
    executionTimeout: 300,
    maxRows: 10000,
  });

  useEffect(() => {
    if (isOpen) {
      const loadDataSources = async () => {
        try {
          const res = await api.sharedQueries.getDataSources();
          setDataSources(res.data?.data || res.data || []);
        } catch {
          setDataSources([
            { DataSourceID: 1, SourceName: 'Main Database', SourceType: 'sqlserver' },
          ]);
        }
      };
      loadDataSources();
      setActiveTab('details');
      setError(null);
      setValidationResult(null);
      setTestResults(null);
    }
  }, [isOpen]);

  useEffect(() => {
    setLineCount(Math.max((formData.sqlQuery || '').split('\n').length, 1));
  }, [formData.sqlQuery]);

  const handleScroll = useCallback(() => {
    if (textareaRef.current && highlightRef.current) {
      highlightRef.current.scrollTop = textareaRef.current.scrollTop;
      highlightRef.current.scrollLeft = textareaRef.current.scrollLeft;
    }
    if (textareaRef.current && lineNumberRef.current) {
      lineNumberRef.current.scrollTop = textareaRef.current.scrollTop;
    }
  }, []);

  const handleKeyDown = (e) => {
    if (e.key === 'Tab') {
      e.preventDefault();
      const { selectionStart, selectionEnd } = e.target;
      const newValue =
        formData.sqlQuery.substring(0, selectionStart) +
        '  ' +
        formData.sqlQuery.substring(selectionEnd);
      setFormData((prev) => ({ ...prev, sqlQuery: newValue }));
      requestAnimationFrame(() => {
        e.target.selectionStart = e.target.selectionEnd = selectionStart + 2;
      });
    }
  };

  const handleValidate = async () => {
    try {
      setValidating(true);
      setValidationResult(null);
      const res = await api.sharedQueries.validate(formData.sqlQuery, formData.dataSourceId);
      setValidationResult(res.data?.data || res.data);
    } catch (err) {
      setValidationResult({
        isValid: false,
        error: err.response?.data?.error || 'Validation failed',
      });
    } finally {
      setValidating(false);
    }
  };

  const handleTestExecute = async () => {
    if (!formData.sqlQuery.trim()) return;
    try {
      setLoading(true);
      setTestResults(null);
      // First create or validate, then we'll show results inline
      const res = await api.sharedQueries.validate(formData.sqlQuery, formData.dataSourceId);
      const validationData = res.data?.data || res.data;
      if (validationData?.isValid) {
        setTestResults({ success: true, message: 'Query syntax is valid' });
      } else {
        const errorMsg =
          validationData?.error ||
          (validationData?.errors && validationData.errors.join(', ')) ||
          'Query validation failed';
        setTestResults({ success: false, message: errorMsg });
      }
    } catch (err) {
      setTestResults({
        success: false,
        message: err.response?.data?.error || 'Test execution failed',
      });
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async () => {
    if (!formData.queryName.trim()) {
      setError('Query name is required');
      return;
    }
    if (!formData.sqlQuery.trim()) {
      setError('SQL query is required');
      return;
    }

    try {
      setLoading(true);
      setError(null);
      const response = await api.sharedQueries.create({
        queryName: formData.queryName.trim(),
        description: formData.description.trim(),
        sqlQuery: formData.sqlQuery,
        dataSourceId: formData.dataSourceId,
        isPublic: formData.isPublic,
        executionTimeout: formData.executionTimeout,
        maxRows: formData.maxRows,
      });
      onCreated(response.data?.data || response.data);
      onClose();
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to create query');
    } finally {
      setLoading(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      <div
        className="absolute inset-0 bg-gray-900/60 dark:bg-black/60 backdrop-blur-sm"
        onClick={onClose}
      />
      <div className="relative bg-surface rounded-xl shadow-2xl max-w-4xl w-full mx-4 max-h-[90vh] flex flex-col">
        {/* Header */}
        <div className="flex justify-between items-center p-5 border-b">
          <div className="flex items-center space-x-3">
            <div className="w-8 h-8 rounded-lg bg-blue-100 text-blue-600 flex items-center justify-center">
              <FaDatabase />
            </div>
            <h2 className="text-lg font-bold text-gray-900">Create Query</h2>
          </div>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600 p-2">
            <FaTimes />
          </button>
        </div>

        {/* Tabs */}
        <div className="flex border-b px-5">
          <button
            onClick={() => setActiveTab('details')}
            className={`px-4 py-2.5 text-sm font-medium border-b-2 transition-colors ${
              activeTab === 'details'
                ? 'border-primary-500 text-primary-600'
                : 'border-transparent text-gray-500 hover:text-gray-700'
            }`}
          >
            Details
          </button>
          <button
            onClick={() => setActiveTab('sql')}
            className={`px-4 py-2.5 text-sm font-medium border-b-2 transition-colors ${
              activeTab === 'sql'
                ? 'border-primary-500 text-primary-600'
                : 'border-transparent text-gray-500 hover:text-gray-700'
            }`}
          >
            SQL Query
          </button>
          <button
            onClick={() => setActiveTab('settings')}
            className={`px-4 py-2.5 text-sm font-medium border-b-2 transition-colors ${
              activeTab === 'settings'
                ? 'border-primary-500 text-primary-600'
                : 'border-transparent text-gray-500 hover:text-gray-700'
            }`}
          >
            Settings
          </button>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-5">
          {error && (
            <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg mb-4 text-sm">
              {error}
            </div>
          )}

          {activeTab === 'details' && (
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Query Name *</label>
                <input
                  type="text"
                  value={formData.queryName}
                  onChange={(e) => setFormData((prev) => ({ ...prev, queryName: e.target.value }))}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                  placeholder="e.g., Daily Weight Summary"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Description</label>
                <textarea
                  value={formData.description}
                  onChange={(e) =>
                    setFormData((prev) => ({ ...prev, description: e.target.value }))
                  }
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                  rows="3"
                  placeholder="What does this query return?"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Data Source</label>
                <ThemedSelect
                  value={formData.dataSourceId}
                  onChange={(e) =>
                    setFormData((prev) => ({ ...prev, dataSourceId: parseInt(e.target.value) }))
                  }
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                >
                  {dataSources.map((ds) => (
                    <option
                      key={ds.data_source_id || ds.DataSourceID}
                      value={ds.data_source_id || ds.DataSourceID}
                    >
                      {ds.source_name || ds.SourceName} ({ds.source_type || ds.SourceType})
                    </option>
                  ))}
                </ThemedSelect>
              </div>
              <div className="flex items-center space-x-2">
                <input
                  type="checkbox"
                  id="isPublic"
                  checked={formData.isPublic}
                  onChange={(e) => setFormData((prev) => ({ ...prev, isPublic: e.target.checked }))}
                  className="rounded border-gray-300 text-primary-600 focus:ring-primary-500"
                />
                <label htmlFor="isPublic" className="text-sm text-gray-700">
                  Public query (visible to all users)
                </label>
              </div>
            </div>
          )}

          {activeTab === 'sql' && (
            <div className="space-y-3">
              <div className="rounded-lg overflow-hidden border border-gray-700/50 bg-[#282c34]">
                <div className="flex items-center justify-between px-3 py-1.5 bg-[#21252b] border-b border-gray-700/50">
                  <span className="text-[11px] font-medium text-gray-400">SQL Query</span>
                  <div className="flex items-center space-x-2">
                    <button
                      onClick={handleValidate}
                      disabled={validating}
                      className="text-[11px] text-gray-400 hover:text-gray-200 flex items-center space-x-1 px-2 py-1 rounded bg-gray-700/50 hover:bg-gray-600/50 transition-colors"
                    >
                      {validating ? (
                        <FaSpinner className="animate-spin w-3 h-3" />
                      ) : (
                        <FaCheck className="w-3 h-3" />
                      )}
                      <span>Validate</span>
                    </button>
                    <button
                      onClick={handleTestExecute}
                      disabled={loading}
                      className="text-[11px] text-gray-400 hover:text-gray-200 flex items-center space-x-1 px-2 py-1 rounded bg-gray-700/50 hover:bg-gray-600/50 transition-colors"
                    >
                      {loading ? (
                        <FaSpinner className="animate-spin w-3 h-3" />
                      ) : (
                        <FaPlay className="w-3 h-3" />
                      )}
                      <span>Test</span>
                    </button>
                  </div>
                </div>
                <div className="relative" style={{ height: '300px' }}>
                  <div
                    ref={lineNumberRef}
                    className="absolute left-0 top-0 bottom-0 w-10 bg-[#282c34] border-r border-gray-700/30 overflow-hidden pointer-events-none z-10"
                  >
                    <div className="pt-3 pb-3">
                      {Array.from({ length: lineCount }, (_, i) => (
                        <div
                          key={i}
                          className="text-right pr-2 text-[11px] leading-5 text-gray-600 select-none font-mono"
                        >
                          {i + 1}
                        </div>
                      ))}
                    </div>
                  </div>
                  <pre
                    ref={highlightRef}
                    className="absolute inset-0 pl-12 pt-3 pr-3 pb-3 overflow-auto font-mono text-[13px] leading-5 text-[#abb2bf] whitespace-pre-wrap break-words pointer-events-none"
                    dangerouslySetInnerHTML={{ __html: highlightSQL(formData.sqlQuery) + '\n' }}
                  />
                  <textarea
                    ref={textareaRef}
                    value={formData.sqlQuery}
                    onChange={(e) => setFormData((prev) => ({ ...prev, sqlQuery: e.target.value }))}
                    onScroll={handleScroll}
                    onKeyDown={handleKeyDown}
                    className="absolute inset-0 pl-12 pt-3 pr-3 pb-3 w-full h-full resize-none bg-transparent text-transparent caret-[#528bff] font-mono text-[13px] leading-5 outline-none border-0"
                    spellCheck={false}
                    autoComplete="off"
                    autoCorrect="off"
                    autoCapitalize="off"
                  />
                </div>
              </div>

              {validationResult && (
                <div
                  className={`px-3 py-2 rounded text-sm ${
                    validationResult.isValid
                      ? 'bg-green-50 border border-green-200 text-green-700'
                      : 'bg-red-50 border border-red-200 text-red-700'
                  }`}
                >
                  {validationResult.isValid
                    ? 'Query syntax is valid'
                    : validationResult.error ||
                      (validationResult.errors && validationResult.errors.join(', ')) ||
                      'Validation failed'}
                </div>
              )}

              {testResults && (
                <div
                  className={`px-3 py-2 rounded text-sm ${
                    testResults.success
                      ? 'bg-green-50 border border-green-200 text-green-700'
                      : 'bg-red-50 border border-red-200 text-red-700'
                  }`}
                >
                  {testResults.message}
                </div>
              )}
            </div>
          )}

          {activeTab === 'settings' && (
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Execution Timeout (seconds)
                </label>
                <input
                  type="number"
                  value={formData.executionTimeout}
                  onChange={(e) =>
                    setFormData((prev) => ({
                      ...prev,
                      executionTimeout: parseInt(e.target.value) || 300,
                    }))
                  }
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                  min="10"
                  max="600"
                />
                <p className="text-xs text-gray-500 mt-1">
                  Maximum time for query execution (10-600 seconds)
                </p>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Maximum Rows</label>
                <input
                  type="number"
                  value={formData.maxRows}
                  onChange={(e) =>
                    setFormData((prev) => ({ ...prev, maxRows: parseInt(e.target.value) || 10000 }))
                  }
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                  min="1"
                  max="100000"
                />
                <p className="text-xs text-gray-500 mt-1">Maximum rows returned by the query</p>
              </div>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="flex justify-end space-x-3 p-5 border-t bg-gray-50">
          <button
            onClick={onClose}
            className="px-4 py-2 text-gray-700 bg-surface border border-gray-300 hover:bg-gray-50 rounded-lg transition-colors"
            disabled={loading}
          >
            Cancel
          </button>
          <button
            onClick={handleSubmit}
            className="px-4 py-2 bg-primary-500 hover:bg-primary-600 text-white rounded-lg transition-colors disabled:opacity-50 flex items-center space-x-2"
            disabled={loading}
          >
            {loading && <FaSpinner className="animate-spin" />}
            <span>{loading ? 'Creating...' : 'Create Query'}</span>
          </button>
        </div>
      </div>
    </div>
  );
};

export default CreateSharedQueryModal;
